import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requestCode, verifyCode } from "../lib/auth";

type Stage = "enter-id" | "enter-code";

export default function Login() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("enter-id");
  const [identifier, setIdentifier] = useState("");
  const [maskedDest, setMaskedDest] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmitId(e: FormEvent) {
    e.preventDefault();
    if (!identifier.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await requestCode(identifier.trim());
      setMaskedDest(resp.masked_destination);
      setStage("enter-code");
    } catch (err: any) {
      setError(err.message || "Could not send code.");
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await verifyCode(identifier.trim(), code.trim());
      // Redirect all successful logins to / where the PortalHub will automatically
      // evaluate roles and execute the auto-bypass check if applicable.
      navigate("/", { replace: true });
    } catch (err: any) {
      setError(err.message || "Invalid code.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={styles.shell}>
      <div style={styles.card}>
        <h1 style={styles.title}>Station</h1>
        <p style={styles.subtitle}>The Sentinel Room — staff access only</p>

        {stage === "enter-id" && (
          <form onSubmit={onSubmitId} style={styles.form}>
            <label style={styles.label}>Email or phone</label>
            <input
              autoFocus
              autoComplete="username"
              inputMode="email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="you@sentinelroom.com or 816 555 0100"
              style={styles.input}
              disabled={busy}
            />
            <button type="submit" style={styles.primaryBtn} disabled={busy || !identifier.trim()}>
              {busy ? "Sending…" : "Send code"}
            </button>
          </form>
        )}

        {stage === "enter-code" && (
          <form onSubmit={onSubmitCode} style={styles.form}>
            <p style={styles.hint}>Sent a 6-digit code to {maskedDest}</p>
            <label style={styles.label}>Code</label>
            <input
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="• • • • • •"
              style={{ ...styles.input, letterSpacing: "0.4em", textAlign: "center", fontSize: 22 }}
              disabled={busy}
            />
            <button type="submit" style={styles.primaryBtn} disabled={busy || code.length < 6}>
              {busy ? "Verifying…" : "Sign in"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStage("enter-id");
                setCode("");
                setError(null);
              }}
              style={styles.linkBtn}
              disabled={busy}
            >
              Use a different email/phone
            </button>
          </form>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <hr style={styles.divider} />

        <p style={styles.footnote}>
          Setting up a tablet?{" "}
          <Link to="/set-station-token" style={styles.footnoteLink}>
            Paste tablet token →
          </Link>
        </p>
      </div>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 380,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 24,
  },
  title: {
    fontFamily: "Georgia, serif",
    margin: "0 0 4px",
    fontSize: 32,
    color: "var(--gold)",
    letterSpacing: -0.5,
  },
  subtitle: {
    margin: "0 0 24px",
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 600,
  },
  form: { display: "grid", gap: 10 },
  label: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--muted)",
    fontWeight: 800,
  },
  input: {
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "0 14px",
    outline: "none",
    fontSize: 16,
  },
  primaryBtn: {
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--gold)",
    background: "var(--gold)",
    color: "#0D0B08",
    fontSize: 14,
    fontWeight: 800,
    marginTop: 4,
  },
  linkBtn: {
    height: 36,
    borderRadius: 12,
    border: 0,
    background: "transparent",
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 700,
  },
  hint: {
    margin: 0,
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 600,
  },
  error: {
    marginTop: 12,
    padding: 10,
    background: "rgba(224, 80, 64, 0.12)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    borderRadius: 10,
    color: "#FF8A7A",
    fontSize: 13,
    fontWeight: 600,
  },
  divider: {
    margin: "24px 0 16px",
    border: 0,
    borderTop: "1px solid var(--border)",
  },
  footnote: {
    margin: 0,
    color: "var(--muted)",
    fontSize: 12,
    textAlign: "center",
  },
  footnoteLink: { color: "var(--gold)", fontWeight: 700 },
};
